import { BreadcrumbBox } from '@/features/Breadcrumb';
import { CTA } from '@/features/CTA';
import { FeaturedContent } from '@/features/FeaturedContent';
import { SEO } from '@/features/SEO';
import Button from '@/features/shared/Button';
import ContentContainer from '@/features/shared/ContentContainer';
import { MuiContainer } from '@/features/shared/material-ui';
import { ColorNames, SpacingSizes, Theme } from '@/utils/constants';
import { getDocumentBasedImage } from '@/utils/helpers';
import { resolveTheme } from '@/utils/resolvers';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import clsx from 'clsx';
import React, { memo, useEffect, useMemo } from 'react';
import { CustomerStoryProps } from './CustomerStory.interface';
import useStyles from './CustomerStory.styles';
import { HeaderContent } from './HeaderContent';
import { MainContent } from './MainContent';

const resolvePropsBasedOn = ({
  detailContent,
  hasRelatedContent,
  relatedContainerBackground,
}: any) => {
  const defaultProps = {
    container: 'small',
    backgroundColor: ColorNames.darkGray,
    theme: Theme.dark,
    bottomSpacing: SpacingSizes.small,
  };

  const lastItem =
    detailContent && detailContent.length > 0 ? detailContent.slice(-1)[0] : undefined;

  if (hasRelatedContent) {
    const backgroundColor = relatedContainerBackground || defaultProps.backgroundColor;
    const theme = resolveTheme('bg-color')({ rawVal: relatedContainerBackground });

    return {
      container: 'large',
      backgroundColor,
      theme,
      bottomSpacing: SpacingSizes.none,
    };
  }

  if (lastItem) {
    return {
      container: lastItem?.insetWidth || defaultProps.container,
      backgroundColor: lastItem?.bottomBackgroundColor,
      theme: resolveTheme('bg-color')({ rawVal: lastItem?.bottomBackgroundColor }),
      bottomSpacing: defaultProps.bottomSpacing,
    };
  }

  return defaultProps;
};

const CustomerStory = ({
  cta = {},
  logo,
  headline,
  includeBreadcrumb,
  shortDescription,
  primaryButton,
  downloadPDF: pdfDocument,
  includeDownload = false,
  pageMetadata,
  relatedContent,
  relatedContentLink,
  detailContent,
  config,
  labels,
  introductoryContainerBackground,
  introductoryContainerBottomSpacing,
  relatedContainerBackground,
  relatedContainerTopSpacing,
  relatedContainerBottomSpacing,
  contentButtons,
  theme,
  relatedContentTheme,
  cardColor,
  backLink,
  cardImage = {},
}: Br.PropsWithContentButtons<CustomerStoryProps>) => {
  const classes = useStyles({});
  const { downloadIcon } = config || {};
  const { download_text: downloadText = 'Download' } = labels || {};
  const hasRelatedContent = useMemo(() => !!relatedContent?.length, [relatedContent?.length]);
  const bgBreadcrumbTheme = resolveTheme('bg-color')({ rawVal: introductoryContainerBackground });

  useEffect(() => window.scrollTo(0, 0), [pageMetadata]);

  const headerContentProps = {
    logo,
    headline,
    shortDescription,
    primaryButton,
    downloadLink: pdfDocument,
    includeDownload,
    downloadIcon,
    downloadText,
    theme,
  };

  const mainContentProps = {
    detailContent,
  };

  const backLinkProps = useMemo(
    () =>
      resolvePropsBasedOn({
        detailContent,
        hasRelatedContent,
        relatedContainerBackground,
      }),
    [detailContent, hasRelatedContent, relatedContainerBackground],
  );

  const relatedContentProps = useMemo(
    () => ({
      theme: relatedContentTheme,
      template: 'grid',
      curatedList: {
        listTitle: labels?.related_content || 'Related Customer stories',
        contentList: relatedContent,
        cta: relatedContentLink,
      },
      isCardImages: true,
      presentation: {
        topSpacing: relatedContainerTopSpacing || SpacingSizes.small,
        bottomSpacing: relatedContainerBottomSpacing || SpacingSizes.small,
        topBackgroundColor: relatedContainerBackground,
        bottomBackgroundColor: relatedContainerBackground,
      },
      cardTheme: relatedContentTheme,
      showCaption: false,
      cardColor,
    }),
    [
      cardColor,
      labels?.related_content,
      relatedContainerBackground,
      relatedContainerBottomSpacing,
      relatedContainerTopSpacing,
      relatedContent,
      relatedContentLink,
      relatedContentTheme,
    ],
  );

  const metadata = {
    ...pageMetadata,
    metaTitle: pageMetadata?.metaTitle || headline,
    description: pageMetadata?.description || shortDescription,
    socialMediaImage: getDocumentBasedImage({ pageMetadata, cardImage }),
  };

  const {
    nonThemeBackgroundColor,
    backgroundColor,
    topSpacing = SpacingSizes.small,
    containerTopBackgroundColor,
    textColor,
    content: { content: ctaContent } = { content: {} },
    ...ctaProps
  } = cta;
  const ctaContainerProps = {
    topSpacing,
    bottomSpacing: SpacingSizes.small,
    topBackgroundColor: containerTopBackgroundColor,
    bottomBackgroundColor: ColorNames.black,
  };

  const ctaTheme = useMemo(() => resolveTheme('text-color')({ rawVal: textColor }), [textColor]);

  const hasCtaContent = useMemo(() => !!Object.keys(ctaContent).length, [ctaContent]);

  const rootClassName = clsx(classes.root, `${theme}-theme`);

  return (
    <article className={rootClassName} id="customer-story" data-datalayer-position="customer_story">
      <ContentContainer
        topSpacing={SpacingSizes.small}
        bottomSpacing={introductoryContainerBottomSpacing || SpacingSizes.small}
        topBackgroundColor={introductoryContainerBackground || ColorNames.eggshell}
        bottomBackgroundColor={introductoryContainerBackground || ColorNames.eggshell}
      >
        <div className={classes.container}>
          {includeBreadcrumb && (
            <MuiContainer>
              <BreadcrumbBox className={classes.breadcrumb} theme={bgBreadcrumbTheme} />
            </MuiContainer>
          )}
          <MuiContainer className="medium">
            <header data-datalayer-position="header_content">
              {contentButtons?.content}
              <HeaderContent {...headerContentProps} />
            </header>
          </MuiContainer>
        </div>
      </ContentContainer>

      <section>
        <MainContent {...mainContentProps} />
      </section>

      {backLink && (
        <ContentContainer
          topSpacing={SpacingSizes.small}
          bottomSpacing={hasRelatedContent ? SpacingSizes.none : SpacingSizes.small}
          topBackgroundColor={backLinkProps.backgroundColor}
          bottomBackgroundColor={backLinkProps.backgroundColor}
        >
          <MuiContainer className={backLinkProps.container}>
            <Button
              className={classes.backLink}
              link={backLink}
              variant="link"
              startIcon={<ChevronLeftIcon color="inherit" fontSize="small" />}
              theme={backLinkProps.theme}
            />
          </MuiContainer>
        </ContentContainer>
      )}

      {(hasRelatedContent || hasCtaContent) && (
        <footer className={classes.footer}>
          {hasRelatedContent && (
            <section data-datalayer-position="featured_content" className={classes.relatedContent}>
              <FeaturedContent {...relatedContentProps} />
            </section>
          )}
          {hasCtaContent && (
            <ContentContainer {...ctaContainerProps}>
              <MuiContainer className="medium">
                <section data-datalayer-position="cta_component">
                  <CTA
                    theme={ctaTheme}
                    {...ctaProps}
                    {...ctaContent}
                    backgroundColor={backgroundColor}
                    nonThemeBackgroundColor={nonThemeBackgroundColor}
                  />
                </section>
              </MuiContainer>
            </ContentContainer>
          )}
        </footer>
      )}
      {metadata && <SEO metadata={metadata} isDocumentBasedPage />}
    </article>
  );
};

export default memo(CustomerStory);
